import { Component, OnInit, ViewChild } from '@angular/core';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { faPlus, faComments, faChartLine, faTrash, faTag, faUnlink, faPen } from '@fortawesome/free-solid-svg-icons';
import { HeaderActionsComponent, HeaderActionsConfig } from '../../components/header-actions/header-actions.component';
import { ItemListComponent, ListItem, ListConfig } from '../../components/item-list/item-list.component';
import { InteractionsService, Interaction } from '../../services/interactions.service';
import { RetrieversService } from '../../services/retrievers.service';
import { ProcessingsService } from '../../services/processings.service';
import { DateFormatterService } from '../../services/date-formatter.service';
import { ModalCreateInteractionComponent } from '../../components/modal-create-interaction/modal-create-interaction.component';
import { ModalDeleteConfirmComponent } from '../../components/modal-delete-confirm/modal-delete-confirm.component';
import { ModalRenameConfirmComponent } from '../../components/modal-rename-confirm/modal-rename-confirm.component';
import { AlertService } from '../../components/alert-service/alert-service';
import { TagManagerComponent, TaggableItem } from '../../components/tag-manager/tag-manager.component';
import { ActivatedRoute, Router } from '@angular/router';
import { TenantService } from '../../services/tenant.service';
import { Tag } from '../../services/tags.service';
import { SearchFilters } from '../../components/search/search.component';
import { PaginationComponent } from '../../components/pagination/pagination.component';

@Component({
  selector: 'app-interactions',
  standalone: true,
  imports: [
    FontAwesomeModule,
    TranslateModule,
    HeaderActionsComponent,
    ItemListComponent,
    ModalCreateInteractionComponent,
    ModalDeleteConfirmComponent,
    ModalRenameConfirmComponent,
    TagManagerComponent,
    PaginationComponent
],
  templateUrl: './interactions.component.html',
  styleUrls: ['./interactions.component.scss']
})
export class InteractionsComponent implements OnInit {
  @ViewChild(TagManagerComponent) tagManager!: TagManagerComponent;

  interactions: Interaction[] = [];
  loading = false;
  showCreateModal = false;
  showDeleteModal = false;
  interactionToDelete: ListItem | null = null;
  interactionImpactItems: string[] = [];
  interactionId = '';

  showRenameModal = false;
  renaming = false;
  interactionToRename: ListItem | null = null;
  tagEntityType = 'interactions';

  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;

  faChartLine = faChartLine;
  faComments = faComments;
  faTag = faTag;
  faUnlink = faUnlink;

  currentFilters: SearchFilters = {
    searchTerm: '',
    selectedTag: '',
    selectedType: ''
  };

  headerConfig: HeaderActionsConfig = {
    addButton: {
      type: 'add' as const,
      text: 'BUTTON.ADD',
      icon: faPlus,
      visible: true,
      disabled: false
    },
    helpButton: {
      type: 'help' as const,
      icon: faComments,
      visible: false,
      disabled: true
    },
    showAddButton: true,
    showSearch: true,
    showTagFilter: true,
    showBackButton: false,
    searchConfig: {
      showTextSearch: true,
      showTagFilter: true,
      textPlaceholder: 'SEARCH.PLACEHOLDER.INTERACTIONS',
      tagPlaceholder: 'SEARCH.PLACEHOLDER.TAGS',
      showTypeFilter: true,
      typePlaceholder: 'SIMULATIONS.TYPE.ALL',
      typeOptions: [
        { value: 'extraction', label: 'SIMULATIONS.TYPE.EXTRACTION' },
        { value: 'classification', label: 'SIMULATIONS.TYPE.CLASSIFICATION' },
        { value: 'retriever', label: 'SIMULATIONS.TYPE.RETRIEVER' },
        { value: 'processing', label: 'SIMULATIONS.TYPE.PROCESSING' }
      ]
    }
  };

  listConfig: ListConfig = {
    itemIcon: faChartLine,
    itemIconClass: 'interaction-icon',
    showMetadata: true,
    showType: true,
    typeLabels: {
      extraction: 'SIMULATIONS.TYPE.EXTRACTION',
      classification: 'SIMULATIONS.TYPE.CLASSIFICATION',
      retriever: 'SIMULATIONS.TYPE.RETRIEVER',
      processing: 'SIMULATIONS.TYPE.PROCESSING',
    },
    emptyMessage: 'MESSAGE.NO.INTERACTIONS',
    actions: [
      {
        id: 'open',
        label: 'Abrir',
        icon: faChartLine,
        cssClass: 'open-item',
        visible: () => false
      },
      {
        id: 'link-tag',
        label: 'BUTTON.LINK.TAG',
        icon: faTag,
        cssClass: 'link-tag-item'
      },
      {
        id: 'unlink-tag',
        label: 'BUTTON.UNLINK.TAG',
        icon: faUnlink,
        cssClass: 'unlink-tag-item'
      },
      {
        id: 'rename',
        label: 'BUTTON.RENAME',
        icon: faPen,
        cssClass: 'rename-item'
      },
      {
        id: 'delete',
        label: 'BUTTON.DELETE',
        icon: faTrash,
        cssClass: 'delete-item'
      }
    ]
  };

  constructor(
    private service: InteractionsService,
    private retrieversService: RetrieversService,
    private processingsService: ProcessingsService,
    private translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private dateFormatter: DateFormatterService,
    private tenantService: TenantService,
    private alert: AlertService
  ) {}

  private isRetriever(item: ListItem): boolean {
    return item?.type === 'retriever';
  }

  private isProcessing(item: ListItem): boolean {
    return item?.type === 'processing';
  }

  ngOnInit(): void {
    this.interactionId = this.route.snapshot.params['id'];
    this.loadInteractions();
  }

  loadInteractions(): void {
    this.loading = true;
    const tagFilter = this.currentFilters.selectedTag?.trim() || undefined;
    const searchFilter = this.currentFilters.searchTerm?.trim() || undefined;
    
    const typeFilter = this.currentFilters.selectedType?.trim() || undefined;
    this.service.list(tagFilter, searchFilter, this.currentPage, this.pageSize, typeFilter).subscribe({
      next: (response) => {
        this.interactions = response.interactions || [];
        this.totalItems = response.total;
        this.totalPages = response.total_pages;
        this.loading = false;
      },
      error: () => {
        this.interactions = [];
        this.totalItems = 0;
        this.totalPages = 0;
        this.loading = false;
      }
    });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadInteractions();
  }
  
  onFiltersChanged(filters: SearchFilters) {
    this.currentFilters = filters;
    this.currentPage = 1;
    this.loadInteractions();
  }

  onSearchCleared() {
    this.currentFilters.searchTerm = '';
    this.currentPage = 1;
    this.loadInteractions();
  }

  onTagFilterCleared() {
    this.currentFilters.selectedTag = '';
    this.currentPage = 1;
    this.loadInteractions();
  }

  onAllFiltersCleared() {
    this.currentFilters = { searchTerm: '', selectedTag: '', selectedType: '' };
    this.currentPage = 1;
    this.loadInteractions();
  }

  get interactionsAsListItems(): ListItem[] {
    return this.interactions
      .map(i => ({
        id: i.id,
        name: i.name,
        type: i['simulation_type'] || 'extraction',
        created_date: i['created_at'] ? this.dateFormatter.formatDate(i['created_at']) : '',
        created_time: i['created_at'] ? this.dateFormatter.formatTime(i['created_at']) : '',
        tag: i['tag'] || null
      }));
  }

  onAddInteraction(): void {
    this.showCreateModal = true;
  }

  onModalCreated(): void {
    this.showCreateModal = false;
    this.loadInteractions();
  }

  onModalClose(): void {
    this.showCreateModal = false;
  }

  onItemAction(event: { action: string, item: ListItem }) {
    if (event.action === 'open') {
      const route = this.tenantService.buildRoute(`/simulations/${event.item.id}`);
      this.router.navigate(route, {
        state: { interaction: event.item }
      });
      return;
    }
    if (event.action === 'delete') {
      this.interactionToDelete = event.item;
      this.interactionImpactItems = (this.isRetriever(event.item) || this.isProcessing(event.item))
        ? []
        : [
            this.translate.instant('INTERACTIONS.DELETE_IMPACT.RESULTS'),
            this.translate.instant('INTERACTIONS.DELETE_IMPACT.ARCHIVES_UNLINKED'),
            this.translate.instant('INTERACTIONS.DELETE_IMPACT.MODEL_KEPT'),
          ];
      this.showDeleteModal = true;
    }
    if (event.action === 'link-tag') {
      this.handleLinkTag(event.item);
    }
    if (event.action === 'unlink-tag') {
      this.handleUnlinkTag(event.item);
    }
    if (event.action === 'rename') {
      this.renameInteraction(event.item);
    }
  }

  onItemClick(item: ListItem) {
    if (this.isRetriever(item)) {
      this.router.navigate(this.tenantService.buildRoute(`/retrievers/${item.id}`));
      return;
    }

    if (this.isProcessing(item)) {
      this.router.navigate(this.tenantService.buildRoute(`/processings/${item.id}`));
      return;
    }

    const typeSegment = item.type === 'classification' ? '/classification' : '/extraction';
    const route = this.tenantService.buildRoute(`/simulations${typeSegment}/${item.id}`);
    this.router.navigate(route, {
      state: { interactionName: item.name }
    });
  }

  onDeleteConfirm() {
    if (!this.interactionToDelete) {
      return;
    }

    const item = this.interactionToDelete;
    const request$ = this.isRetriever(item)
      ? this.retrieversService.delete(String(item.id))
      : this.isProcessing(item)
        ? this.processingsService.delete(String(item.id))
        : this.service.delete(item.id);

    request$.subscribe({
      next: () => {
        this.loadInteractions();
        this.showDeleteModal = false;
        this.interactionToDelete = null;
      },
      error: () => {
        this.showDeleteModal = false;
        this.interactionToDelete = null;
      }
    });
  }
  
  onDeleteClose() {
    this.showDeleteModal = false;
    this.interactionToDelete = null;
    this.interactionImpactItems = [];
  }

  handleLinkTag(item: ListItem): void {
    this.tagEntityType = this.isRetriever(item) ? 'retrievers' : this.isProcessing(item) ? 'processings' : 'interactions';
    const taggableItem: TaggableItem = {
      id: String(item.id),
      name: item.name,
      tag: item.tag as Tag
    };
    this.tagManager?.openLinkModal(taggableItem);
  }

  handleUnlinkTag(item: ListItem): void {
    this.tagEntityType = this.isRetriever(item) ? 'retrievers' : this.isProcessing(item) ? 'processings' : 'interactions';
    const taggableItem: TaggableItem = {
      id: String(item.id),
      name: item.name,
      tag: item.tag as Tag
    };
    this.tagManager?.openUnlinkModal(taggableItem);
  }

  onTagChanged(): void {
    this.loadInteractions();
  }

  renameInteraction(item: ListItem): void {
    this.interactionToRename = item;
    this.showRenameModal = true;
  }

  onRenameClose(): void {
    if (this.renaming) {
      return;
    }
    this.showRenameModal = false;
    this.interactionToRename = null;
  }

  onRenameConfirm(trimmedName: string): void {
    if (!this.interactionToRename) {
      return;
    }

    this.renaming = true;
    const item = this.interactionToRename;
    const request$ = this.isRetriever(item)
      ? this.retrieversService.rename(String(item.id), trimmedName)
      : this.isProcessing(item)
        ? this.processingsService.rename(String(item.id), trimmedName)
        : this.service.rename(item.id, trimmedName);

    request$.subscribe({
      next: () => {
        this.renaming = false;
        this.alert.showSuccess(this.translate.instant('INTERACTIONS.RENAME_SUCCESS'));
        this.onRenameClose();
        this.loadInteractions();
      },
      error: (err) => {
        this.renaming = false;
        this.alert.showError(err, this.translate.instant('INTERACTIONS.RENAME_ERROR'));
      }
    });
  }
}